import React from 'react';
import { MediaLibraryItem, readCostCredits } from '../../../api/media';
import { persistenceStageLabel } from '../../clips/persistenceLabels';

interface MediaMetadataInspectorProps {
  item: MediaLibraryItem;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const SECRET_PIXEL_KEY = /(base64|_b64$|sample_images|image_data|data_uri)/i;

/** Strip legacy/accidental pixel payloads before recipe display or copying. */
export const sanitizeRecipeForDisplay = (value: unknown, depth = 0): unknown => {
  if (depth > 8) return '[nested data omitted]';
  if (Array.isArray(value)) return value.slice(0, 50).map((entry) => sanitizeRecipeForDisplay(entry, depth + 1));
  if (isRecord(value)) {
    const clean: Record<string, unknown> = {};
    Object.entries(value).slice(0, 100).forEach(([key, entry]) => {
      clean[key] = SECRET_PIXEL_KEY.test(key) ? '[pixel data omitted]' : sanitizeRecipeForDisplay(entry, depth + 1);
    });
    return clean;
  }
  if (typeof value === 'string' && value.length > 4000) return `${value.slice(0, 4000)}…`;
  return value;
};

const sanitizeRecipeForCopy = (value: unknown, depth = 0): unknown => {
  if (depth > 20) return '[nested data omitted]';
  if (Array.isArray(value)) return value.map((entry) => sanitizeRecipeForCopy(entry, depth + 1));
  if (isRecord(value)) {
    const clean: Record<string, unknown> = {};
    Object.entries(value).forEach(([key, entry]) => {
      clean[key] = SECRET_PIXEL_KEY.test(key) ? '[pixel data omitted]' : sanitizeRecipeForCopy(entry, depth + 1);
    });
    return clean;
  }
  return value;
};

/** Read a key from a list of candidate record sources (first non-empty wins). */
const readFirst = (sources: Array<Record<string, unknown> | undefined>, keys: string[]): string => {
  for (const source of sources) {
    if (!source) continue;
    for (const key of keys) {
      const value = source[key];
      if (value === null || value === undefined) continue;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) return trimmed;
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }
    }
  }
  return '';
};

const copyToClipboard = (text: string) => {
  if (!text) return;
  try {
    void navigator.clipboard?.writeText(text);
  } catch {
    // Clipboard may be unavailable (insecure context) — fail silently.
  }
};

export const readGenerationRecipe = (item: MediaLibraryItem): Record<string, unknown> | undefined => {
  const metadata = isRecord(item.metadata) ? item.metadata : undefined;
  return isRecord(metadata?.generation_recipe) ? metadata?.generation_recipe : undefined;
};

const recipePrompt = (item: MediaLibraryItem): Record<string, unknown> | undefined => {
  const recipe = readGenerationRecipe(item);
  return isRecord(recipe?.prompt) ? recipe?.prompt : undefined;
};

export const readEffectivePrompt = (item: MediaLibraryItem): string =>
  readFirst([recipePrompt(item)], ['effective']);

export const readEffectiveNegativePrompt = (item: MediaLibraryItem): string =>
  readFirst([recipePrompt(item)], ['negative_effective', 'negative_requested']);

export const copyGenerationRecipe = (item: MediaLibraryItem): void => {
  const recipe = readGenerationRecipe(item);
  if (recipe) copyToClipboard(JSON.stringify(sanitizeRecipeForCopy(recipe), null, 2));
};

const summarizeWeightedItems = (value: unknown, kind: 'adapter' | 'control'): string => {
  if (!Array.isArray(value)) return '';
  return value
    .filter(isRecord)
    .map((entry) => {
      const name = kind === 'adapter'
        ? readFirst([entry], ['artifact_id', 'adapter_id', 'id'])
        : readFirst([entry], ['type', 'model_id']);
      const model = kind === 'control' ? readFirst([entry], ['model_id']) : '';
      const weight = readFirst([entry], ['weight', 'lora_strength', 'requested_weight']);
      if (!name) return '';
      const label = model && model !== name ? `${name} (${model})` : name;
      return weight ? `${label} @ ${weight}` : label;
    })
    .filter(Boolean)
    .join(', ');
};

export interface DetailRow {
  label: string;
  value: string;
  /** Optional deep-link href (e.g. the run that produced this asset). */
  href?: string;
}

/**
 * Defensively extract the generation-lineage rows from a media item's metadata
 * (model, seed, size, steps, run, scene, parent, created…). Shared by the
 * sidebar inspector and the drawer/sheet inspector so both render identical
 * data — a missing key just omits its row.
 */
export const buildDetailRows = (item: MediaLibraryItem): DetailRow[] => {
  const metadata = isRecord(item.metadata) ? item.metadata : undefined;
  const recipe = readGenerationRecipe(item);
  const recipeParams = isRecord(recipe?.params) ? recipe?.params : undefined;
  const recipeBaseModel = isRecord(recipe?.base_model) ? recipe?.base_model : undefined;
  const persistedParams = isRecord(metadata?.params) ? metadata?.params : undefined;
  const outputSpec = isRecord(metadata?.output_spec)
    ? (metadata?.output_spec as Record<string, unknown>)
    : undefined;
  const sources = [recipeParams, persistedParams, metadata, outputSpec];

  const model = readFirst([recipeBaseModel, metadata, outputSpec], ['resolved_id', 'id', 'model', 'model_id', 'model_name', 'modelId']);
  const family = readFirst([recipeBaseModel, metadata], ['family', 'base_model_family']);
  const seed = readFirst(sources, ['seed']);
  const generator = readFirst(sources, ['generator']);
  const provider = readFirst([recipe, metadata, outputSpec], ['provider']);
  const runtime = readFirst([recipe, metadata], ['runtime_id']);
  const source = item.source || readFirst(sources, ['source', 'origin']);
  const width = readFirst(sources, ['width']);
  const height = readFirst(sources, ['height']);
  const dimensions =
    readFirst(sources, ['dimensions']) || (width && height ? `${width}×${height}` : '');
  const steps = readFirst(sources, ['steps', 'num_inference_steps']);
  const guidance = readFirst(sources, ['guidance', 'cfg', 'cfg_scale', 'guidance_scale']);
  const scheduler = readFirst(sources, ['scheduler']);
  const gpu = readFirst(sources, ['gpu']);
  const mimeType = item.mime_type || readFirst(sources, ['mime_type', 'content_type']);

  const runId = item.pipeline_run_id || readFirst(sources, ['pipeline_run_id', 'run_id']);
  const runName = item.pipeline_name || readFirst(sources, ['pipeline_name', 'run_name']);
  const sceneId = readFirst(sources, ['scene_id']);
  const parentId = readFirst(sources, ['parent_media_id']);
  const clipId = item.clip_id || readFirst(sources, ['clip_id']);
  const adapters = summarizeWeightedItems(recipe?.adapters ?? metadata?.adapters, 'adapter');
  const controls = summarizeWeightedItems(recipe?.controls ?? metadata?.controls, 'control');
  const ipAdapter = isRecord(recipe?.ip_adapter) ? recipe?.ip_adapter : undefined;
  const ipScale = readFirst([ipAdapter], ['scale']);
  const persistenceStatus = readFirst([metadata], ['persistence_status']);
  const persistenceStage = readFirst([metadata], ['persistence_error_stage']);
  const persistenceError = readFirst([metadata], ['persistence_error']);

  // Settled generation cost (credits). Prefer the mapped field; fall back to
  // reading metadata directly for items normalized before the field existed.
  const costCredits = item.costCredits ?? readCostCredits(metadata);

  const out: DetailRow[] = [];
  if (model) out.push({ label: 'Model', value: model });
  if (family) out.push({ label: 'Family', value: family });
  if (seed) out.push({ label: 'Seed', value: seed });
  if (costCredits != null) out.push({ label: 'Cost', value: `${costCredits} credits` });
  if (dimensions) out.push({ label: 'Size', value: dimensions });
  if (steps) out.push({ label: 'Steps', value: steps });
  if (guidance) out.push({ label: 'Guidance', value: guidance });
  if (scheduler) out.push({ label: 'Scheduler', value: scheduler });
  if (gpu) out.push({ label: 'GPU', value: gpu });
  if (adapters) out.push({ label: 'LoRA adapters', value: adapters });
  if (controls) out.push({ label: 'ControlNet', value: controls });
  if (ipScale) out.push({ label: 'IP-Adapter', value: `scale ${ipScale}` });
  if (generator) out.push({ label: 'Generator', value: generator });
  if (provider) out.push({ label: 'Provider', value: provider });
  if (runtime) out.push({ label: 'Runtime', value: runtime });
  if (mimeType) out.push({ label: 'Format', value: mimeType });
  if (source) out.push({ label: 'Source', value: source });
  if (runId) {
    out.push({ label: 'Run', value: runName || runId, href: `/?run=${encodeURIComponent(runId)}` });
  }
  if (sceneId) out.push({ label: 'Scene', value: sceneId });
  if (clipId) out.push({ label: 'Clip', value: clipId });
  if (parentId) out.push({ label: 'Parent', value: parentId });
  if (persistenceStatus && persistenceStatus !== 'ready') {
    out.push({ label: 'Persistence', value: persistenceStatus });
  }
  if (persistenceStage) out.push({ label: 'Failure stage', value: persistenceStageLabel(persistenceStage) });
  if (persistenceError) out.push({ label: 'Failure', value: persistenceError });
  if (item.created_at) {
    const when = new Date(item.created_at);
    out.push({
      label: 'Created',
      value: Number.isNaN(when.getTime()) ? item.created_at : when.toLocaleString(),
    });
  }
  return out;
};

export const copyPrompt = (text: string): void => copyToClipboard(text);

/**
 * Read-only inspector that surfaces the generation lineage already stored on a
 * media item — model, seed, dimensions, source, and the run/scene/parent that
 * produced it — which the old sidebar discarded. Everything is read defensively
 * from `metadata` (and a few first-class fields), so a missing key just hides
 * that row; nothing throws on uploads or legacy rows that lack the data.
 */
const MediaMetadataInspector: React.FC<MediaMetadataInspectorProps> = ({ item }) => {
  const rows = React.useMemo<DetailRow[]>(() => buildDetailRows(item), [item]);

  if (rows.length === 0) return null;

  return (
    <div className="pt-1 border-t border-white/10 space-y-1">
      <div className="flex items-center justify-between">
        <p className="attachment-meta font-medium">Details</p>
        {item.prompt ? (
          <button
            type="button"
            onClick={() => copyToClipboard(item.prompt || '')}
            className="attachment-meta hover:text-zinc-200 transition-colors"
            title="Copy prompt to clipboard"
          >
            Copy prompt
          </button>
        ) : null}
      </div>
      <dl className="space-y-0.5">
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-2">
            <dt className="attachment-meta shrink-0">{row.label}</dt>
            <dd className="attachment-meta text-right text-zinc-300 break-all min-w-0">
              {row.href ? (
                <a href={row.href} className="hover:text-zinc-100 underline decoration-dotted">
                  {row.value}
                </a>
              ) : (
                row.value
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export default MediaMetadataInspector;
